// Heliports layer family.
//
// Extracted from addOverlays.ts as HI-1 batch 5 round 2 (audit 2026-05-22).
// Pure mechanical move — every source, layer, paint expression,
// layout property, and minzoom/maxzoom is byte-identical to its
// pre-extraction form. Source-id idempotency guard preserved.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';

/**
 * Mount the heliports source + its layers. Idempotent on the source.
 */
export function addHeliportsLayers(map: MlMap): void {
    if (!map.getSource('heliports')) {
      map.addSource('heliports', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'heliports-icon',
        type: 'symbol',
        source: 'heliports',
        // 2026-05-16: minzoom dropped 10 -> 8 + icon-size bumped per boss
        // feedback ("heliports are small, very hard to see, visible from a
        // lower zoom"). At z=8 over a metro area we show the pads but the
        // server's 200-payload cap keeps the wire bounded.
        minzoom: 8,
        layout: {
          'icon-image': 'heli',
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            8,  0.55,
            10, 0.90,
            12, 1.20,
            14, 1.45,
          ],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'visibility': 'none',
        },
      });
      map.addLayer({
        id: 'heliports-label',
        type: 'symbol',
        source: 'heliports',
        // Labels appear at z>=10 (was 12). Pilots zooming in to a metro
        // area can ID pads by ident before tapping for the full card.
        minzoom: 10,
        layout: {
          'text-field': ['get', 'ident'],
          // 2026-05-16: bumped 9 -> 11 for visibility; matches navaid label
          // size so heliports don't read as second-class on the chart.
          'text-size': 11,
          'text-offset': [0, 1.1],
          'text-anchor': 'top',
          'text-font': ['Noto Sans Regular'],
          'visibility': 'none',
        },
        paint: {
          'text-color': '#fbcfe8',
          'text-halo-color': '#0b0d0f',
          'text-halo-width': 1.2,
        },
      });
    }
}

registerLayerFamily({
  id: 'heliports',
  label: 'Heliports',
  sourceId: 'heliports',
  layerIds: ['heliports-icon', 'heliports-label'],
  mount: (map) => addHeliportsLayers(map),
  visibilitySelector: (l) => l.heliports,
  applyState: (map) => {
    if (!map.getLayer('heliports-icon')) return;
    const show = useLayerVisibilityStore.getState().layers.heliports;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('heliports-icon', 'visibility', v);
    map.setLayoutProperty('heliports-label', 'visibility', v);
  },
  // bboxRefresh + toggle-on handled by MapView's core bbox-fetch loop
  // (heliports use fetchBbox with hospitalsOnly filter, not overlayRuntime).
});
